import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Target,
  TrendingUp,
  Users,
  Banknote,
  BarChart3,
  Download,
  Plus,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

type Branch = { id: number; name: string };

const monthNames = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export interface BranchTargetRow {
  id: number;
  branch_id: number;
  branch: string;
  month: number; // 1..12
  year: number;

  disbursementTarget: number;
  disbursementActual: number;

  leadsTarget: number;
  leadsActual: number;

  customerTarget: number;
  customerActual: number;

  collectionTarget: number; // %
  collectionActual: number; // %
}

type ReportRes = {
  data: BranchTargetRow[];
  month: number;
  year: number;
  branches: Branch[];
};

const fmt = (n: number) => new Intl.NumberFormat("en-KE").format(Math.round(n || 0));
const pct = (actual: number, target: number) => (target === 0 ? 0 : Math.round((actual / target) * 100));

/* ─── chart colors (match Profit/Loss vibe) ─── */
const CHART_COLORS = {
  target: "hsl(210, 80%, 55%)",   // blue
  actual: "hsl(142, 70%, 50%)",   // green
  targetPct: "hsl(30, 90%, 55%)", // orange
  actualPct: "hsl(142, 70%, 50%)" // green
};

const chartGridStroke = "hsl(var(--border))";

const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "12px",
  fontSize: "12px",
};

const legendStyle = { fontSize: 12 };


function statusBadge(actual: number, target: number) {
  const p = pct(actual, target);
  if (p >= 100) return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30">Achieved</Badge>;
  if (p >= 75) return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">On Track</Badge>;
  return <Badge className="bg-red-500/15 text-red-700 border-red-500/30">Behind</Badge>;
}

export default function TargetsPage() {
  const now = new Date();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [targets, setTargets] = useState<BranchTargetRow[]>([]);



  type SortKey = "actual" | "target" | "pct";
const [sortBy, setSortBy] = useState<SortKey>("actual");

const disbChartH = useMemo(() => {
  const rows = targets.map((t) => {
    const target = t.disbursementTarget || 0;
    const actual = t.disbursementActual || 0;
    const p = target === 0 ? 0 : (actual / target) * 100;

    return {
      branch: t.branch,
      target,
      actual,
      pct: Math.round(p),
    };
  });

  rows.sort((a, b) => {
    if (sortBy === "pct") return b.pct - a.pct;
    if (sortBy === "target") return b.target - a.target;
    return b.actual - a.actual; // default: actual
  });

  return rows;
}, [targets, sortBy]);



type CollSortKey = "actual" | "target" | "pct";
const [collSortBy, setCollSortBy] = useState<CollSortKey>("pct");
const [collShowTop, setCollShowTop] = useState<"all" | "top10">("all");

const collectionChartH = useMemo(() => {
  const rows = targets.map((t) => {
    const target = Number(t.collectionTarget || 0);
    const actual = Number(t.collectionActual || 0);
    const p = target === 0 ? 0 : (actual / target) * 100;

    return {
      branch: t.branch,
      target,
      actual,
      pct: Math.round(p),
    };
  });

  rows.sort((a, b) => {
    if (collSortBy === "pct") return b.pct - a.pct;
    if (collSortBy === "target") return b.target - a.target;
    return b.actual - a.actual;
  });

  return collShowTop === "top10" ? rows.slice(0, 10) : rows;
}, [targets, collSortBy, collShowTop]);

const collChartHeight = Math.max(320, collectionChartH.length * 32);

const chartHeight = Math.max(320, disbChartH.length * 32); // 32px per branch row



  // filters
  const [selectedBranch, setSelectedBranch] = useState<string>("all"); // "all" or branch_id
  const [selectedMonth, setSelectedMonth] = useState<string>(monthNames[now.getMonth()]);
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());

  // ui state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // dialog state
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // form
  const [formBranchId, setFormBranchId] = useState<string>(""); // id string
  const [formMonth, setFormMonth] = useState<string>(monthNames[now.getMonth()]);
  const [formYear, setFormYear] = useState<number>(now.getFullYear());
  const [formDisb, setFormDisb] = useState("5000000");
  const [formLeads, setFormLeads] = useState("5000");
  const [formCust, setFormCust] = useState("1000");
  const [formColl, setFormColl] = useState("95");

  // simple reload key
  const [reloadKey, setReloadKey] = useState(0);

  const monthNum = useMemo(() => monthNames.indexOf(selectedMonth) + 1, [selectedMonth]);

  async function loadReport() {
    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<ReportRes>(
        `/targets/targets-report?month=${monthNum}&year=${selectedYear}&branch_id=${selectedBranch}`
      );
      setTargets(json.data || []);
      setBranches(json.branches || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load targets report");
      setTargets([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, monthNum, selectedYear, reloadKey]);

  const totals = useMemo(() => {
    return targets.reduce(
      (acc, t) => ({
        disbTarget: acc.disbTarget + (t.disbursementTarget || 0),
        disbActual: acc.disbActual + (t.disbursementActual || 0),

        leadsTarget: acc.leadsTarget + (t.leadsTarget || 0),
        leadsActual: acc.leadsActual + (t.leadsActual || 0),

        custTarget: acc.custTarget + (t.customerTarget || 0),
        custActual: acc.custActual + (t.customerActual || 0),

        // Weighted collection (by disbursed)
        collTargetSum: acc.collTargetSum + (t.collectionTarget || 0) * (t.disbursementTarget || 0),
        collActualSum: acc.collActualSum + (t.collectionActual || 0) * (t.disbursementActual || 0),

        collTargetDen: acc.collTargetDen + (t.disbursementTarget || 0),
        collActualDen: acc.collActualDen + (t.disbursementActual || 0),
      }),
      {
        disbTarget: 0, disbActual: 0,
        leadsTarget: 0, leadsActual: 0,
        custTarget: 0, custActual: 0,
        collTargetSum: 0, collActualSum: 0,
        collTargetDen: 0, collActualDen: 0,
      }
    );
  }, [targets]);

  const avgCollTarget = totals.collTargetDen === 0 ? 0 : Math.round(totals.collTargetSum / totals.collTargetDen);
  const avgCollActual = totals.collActualDen === 0 ? 0 : Math.round(totals.collActualSum / totals.collActualDen);

  const chartData = useMemo(() => {
    return targets.map((t) => ({
      branch: t.branch,
      "Disb. Target": t.disbursementTarget,
      "Disb. Actual": t.disbursementActual,
    }));
  }, [targets]);

  const collectionChartData = useMemo(() => {
    return targets.map((t) => ({
      branch: t.branch,
      "Target %": t.collectionTarget,
      "Actual %": t.collectionActual,
    }));
  }, [targets]);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Branch Targets Report - ${selectedMonth} ${selectedYear}`, 14, 20);
    doc.setFontSize(10);
    doc.text(
      `Branch: ${selectedBranch === "all" ? "All Branches" : branches.find(b => String(b.id) === selectedBranch)?.name ?? "-"}`,
      14,
      28
    );

    autoTable(doc, {
      startY: 35,
      head: [["Branch", "Disb T", "Disb A", "%", "Leads T", "Leads A", "%", "Cust T", "Cust A", "%", "Coll T%", "Coll A%"]],
      body: targets.map((t) => [
        t.branch,
        fmt(t.disbursementTarget),
        fmt(t.disbursementActual),
        `${pct(t.disbursementActual, t.disbursementTarget)}%`,

        t.leadsTarget,
        t.leadsActual,
        `${pct(t.leadsActual, t.leadsTarget)}%`,

        t.customerTarget,
        t.customerActual,
        `${pct(t.customerActual, t.customerTarget)}%`,

        `${t.collectionTarget}%`,
        `${t.collectionActual}%`,
      ]),
      styles: { fontSize: 7 },
    });

    doc.save(`targets_${selectedYear}_${monthNum}.pdf`);
  };

  const exportExcel = () => {
    const rows = targets.map((t) => ({
      Branch: t.branch,
      Month: `${t.month}-${t.year}`,
      "Disbursement Target": t.disbursementTarget,
      "Disbursement Actual": t.disbursementActual,
      "Disbursement %": `${pct(t.disbursementActual, t.disbursementTarget)}%`,
      "Leads Target": t.leadsTarget,
      "Leads Actual": t.leadsActual,
      "Leads %": `${pct(t.leadsActual, t.leadsTarget)}%`,
      "Customer Target": t.customerTarget,
      "Customer Actual": t.customerActual,
      "Customer %": `${pct(t.customerActual, t.customerTarget)}%`,
      "Collection Target %": `${t.collectionTarget}%`,
      "Collection Actual %": `${t.collectionActual}%`,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Targets");
    XLSX.writeFile(wb, `targets_${selectedYear}_${monthNum}.xlsx`);
  };

  async function handleSaveTarget() {
    if (!formBranchId) {
      setError("Please select branch");
      return;
    }

    const m = monthNames.indexOf(formMonth) + 1;
    if (m < 1) {
      setError("Invalid month");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        branch_id: Number(formBranchId),
        target_month: m,
        target_year: Number(formYear),
        disbursement_target: Number(formDisb) || 0,
        client_target: Number(formCust) || 0,
        lead_target: Number(formLeads) || 0,
        collection_rate: Number(formColl) || 0,
      };

      const res = await apiFetch<any>(`/targets`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      // close + reset
      setAddOpen(false);
      setFormDisb("5000000"); setFormLeads("5000"); setFormCust("1000"); setFormColl("95");

      // refresh report
      setReloadKey((k) => k + 1);

      // simple message
      // (use your snackbar helper if you have one)
     toast.success("Target saved", {
  description: res?.message ?? "Saved successfully",
});
    } catch (e: any) {
      const msg = e?.message || "Failed to save target";
setError(msg);
toast.error("Save failed", { description: msg });
    } finally {
      setSaving(false);
    }
  }

  async function deleteTarget(id: number) {
    if (!confirm("Delete this target?")) return;

    try {
      await apiFetch(`/targets/${id}`, { method: "DELETE" });
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      alert(e?.message || "Failed to delete");
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground flex items-center gap-2"
        >
          <Target className="h-6 w-6 text-primary" /> Branch Targets
          {loading && <span className="text-sm text-muted-foreground font-medium ml-2">Loading...</span>}
        </motion.h1>

        <div className="flex flex-wrap items-center gap-2">
          {/* Branch */}
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Month */}
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthNames.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Year */}
          <Input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value || now.getFullYear()))}
            className="w-28 h-10"
          />

          <Button
            variant="outline"
            size="sm"
            onClick={() => setReloadKey((k) => k + 1)}
            disabled={loading}
          >
            <RefreshCcw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Reload
          </Button>

          {/* Add Target */}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" /> Set Target
              </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Set Branch Target</DialogTitle>
              </DialogHeader>

              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Branch</Label>
                    <Select value={formBranchId} onValueChange={setFormBranchId}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Month</Label>
                    <Select value={formMonth} onValueChange={setFormMonth}>
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthNames.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Year</Label>
                    <Input className="h-10" type="number" value={formYear} onChange={(e) => setFormYear(Number(e.target.value || now.getFullYear()))} />
                  </div>

                  <div>
                    <Label>Collection Target (%)</Label>
                    <Input className="h-10" type="number" placeholder="e.g. 85" value={formColl} onChange={(e) => setFormColl(e.target.value)} />
                  </div>
                </div>

                <div>
                  <Label>Disbursement Target (KES)</Label>
                  <Input className="h-10" type="number" placeholder="e.g. 5000000" value={formDisb} onChange={(e) => setFormDisb(e.target.value)} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Leads Target</Label>
                    <Input className="h-10" type="number" placeholder="e.g. 200" value={formLeads} onChange={(e) => setFormLeads(e.target.value)} />
                  </div>
                  <div>
                    <Label>Customers Target</Label>
                    <Input className="h-10" type="number" placeholder="e.g. 80" value={formCust} onChange={(e) => setFormCust(e.target.value)} />
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4 flex gap-2">
                <DialogClose asChild>
                  <Button variant="outline" disabled={saving}>Cancel</Button>
                </DialogClose>
                <Button onClick={handleSaveTarget} disabled={saving}>
                  {saving ? "Saving..." : "Save Target"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Card className="border-destructive/40">
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Banknote, label: "Disbursement", target: `KES ${fmt(totals.disbTarget)}`, actual: `KES ${fmt(totals.disbActual)}`, percent: pct(totals.disbActual, totals.disbTarget), color: "text-emerald-600" },
          { icon: TrendingUp, label: "Leads", target: fmt(totals.leadsTarget), actual: fmt(totals.leadsActual), percent: pct(totals.leadsActual, totals.leadsTarget), color: "text-blue-600" },
          { icon: Users, label: "Customers", target: fmt(totals.custTarget), actual: fmt(totals.custActual), percent: pct(totals.custActual, totals.custTarget), color: "text-purple-600" },
          { icon: BarChart3, label: "Collection %", target: `${avgCollTarget}%`, actual: `${avgCollActual}%`, percent: avgCollTarget === 0 ? 0 : Math.round((avgCollActual / avgCollTarget) * 100), color: "text-amber-600" },
        ].map((card, i) => (
          <motion.div key={card.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <Card className="shadow-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <card.icon className={`h-5 w-5 ${card.color}`} />
                  <span className="text-sm font-medium text-muted-foreground">{card.label}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Target: {card.target}</span>
                  <span>Actual: {card.actual}</span>
                </div>
                <Progress value={Math.min(card.percent, 100)} className="h-2" />
                <p className={cn(
                  "text-right text-sm font-bold mt-1",
                  card.percent >= 100 ? "text-emerald-700" : card.percent >= 75 ? "text-amber-700" : "text-red-700"
                )}>
                  {card.percent}%
                </p>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="table" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList>
            <TabsTrigger value="table">Target Table</TabsTrigger>
            <TabsTrigger value="charts">Charts</TabsTrigger>
            <TabsTrigger value="report">Report</TabsTrigger>
          </TabsList>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportExcel}>
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF}>
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* Table */}
        <TabsContent value="table">
          <Card className="shadow-card">
            <CardContent className="p-0">
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">Disb. Target</TableHead>
                      <TableHead className="text-right">Disb. Actual</TableHead>
                      <TableHead className="text-right">Leads T/A</TableHead>
                      <TableHead className="text-right">Cust T/A</TableHead>
                      <TableHead className="text-right">Collection %</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {targets.length === 0 && !loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                          No targets set for this selection.
                        </TableCell>
                      </TableRow>
                    ) : (
                      targets.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">{t.branch}</TableCell>
                          <TableCell className="text-right">{fmt(t.disbursementTarget)}</TableCell>
                          <TableCell className="text-right">{fmt(t.disbursementActual)}</TableCell>
                          <TableCell className="text-right">{t.leadsActual}/{t.leadsTarget}</TableCell>
                          <TableCell className="text-right">{t.customerActual}/{t.customerTarget}</TableCell>
                          <TableCell className="text-right">
                            <span className={t.collectionActual >= t.collectionTarget ? "text-emerald-700" : "text-red-700"}>
                              {t.collectionActual}%
                            </span>
                            <span className="text-muted-foreground"> / {t.collectionTarget}%</span>
                          </TableCell>
                          <TableCell className="text-center">{statusBadge(t.disbursementActual, t.disbursementTarget)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteTarget(t.id)}
                              className="inline-flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3 p-4">
                {targets.length === 0 && !loading ? (
                  <p className="text-center text-muted-foreground py-10">
                    No targets set for this selection.
                  </p>
                ) : (
                  targets.map((t) => (
                    <Card key={t.id} className="shadow-card">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{t.branch}</span>
                          {statusBadge(t.disbursementActual, t.disbursementTarget)}
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-muted-foreground">Disb. Target</p>
                            <p className="font-medium">{fmt(t.disbursementTarget)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Disb. Actual</p>
                            <p className="font-medium">{fmt(t.disbursementActual)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Leads</p>
                            <p className="font-medium">{t.leadsActual}/{t.leadsTarget}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Customers</p>
                            <p className="font-medium">{t.customerActual}/{t.customerTarget}</p>
                          </div>
                        </div>

                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">Collection</span>
                            <span className={t.collectionActual >= t.collectionTarget ? "text-emerald-700" : "text-red-700"}>
                              {t.collectionActual}% / {t.collectionTarget}%
                            </span>
                          </div>
                          <Progress value={t.collectionTarget ? Math.min((t.collectionActual / t.collectionTarget) * 100, 100) : 0} className="h-2" />
                        </div>

                        <Button variant="outline" onClick={() => deleteTarget(t.id)} className="w-full">
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Charts */}
        <TabsContent value="charts">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:items-start">
<Card className="shadow-card">
  <CardHeader className="flex flex-row items-center justify-between gap-2">
    <CardTitle className="text-base">Disbursement: Target vs Actual (by Branch)</CardTitle>

    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">Sort:</span>
      <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
        <SelectTrigger className="h-8 w-32 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="actual">Actual</SelectItem>
          <SelectItem value="target">Target</SelectItem>
          <SelectItem value="pct">% Achieved</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </CardHeader>

  <CardContent className="p-0">
    {/* scroll container */}
    <div className=" overflow-y-auto px-1 pb-4">
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={disbChartH}
            layout="vertical"
           margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            barSize={14}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
            <XAxis
              type="number"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
            />
            <YAxis
              type="category"
              dataKey="branch"
              width={110}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={(v: number, name: string) =>
                name === "pct" ? `${v}%` : `KES ${fmt(v)}`
              }
              contentStyle={tooltipStyle}
            />
            <Legend wrapperStyle={legendStyle} />
            <Bar dataKey="target" name="Target" fill={CHART_COLORS.target} radius={[0, 4, 4, 0]} />
            <Bar dataKey="actual" name="Actual" fill={CHART_COLORS.actual} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </CardContent>
</Card>




          <Card className="shadow-card">
  <CardHeader className="flex flex-row items-center justify-between gap-2">
    <CardTitle className="text-base">Collection %: Target vs Actual (by Branch)</CardTitle>

    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">Sort:</span>

      <Select value={collSortBy} onValueChange={(v) => setCollSortBy(v as any)}>
        <SelectTrigger className="h-8 w-32 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="pct">% Achieved</SelectItem>
          <SelectItem value="actual">Actual %</SelectItem>
          <SelectItem value="target">Target %</SelectItem>
        </SelectContent>
      </Select>

      <Select value={collShowTop} onValueChange={(v) => setCollShowTop(v as any)}>
        <SelectTrigger className="h-8 w-24 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          <SelectItem value="top10">Top 10</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </CardHeader>

  <CardContent className="p-0">
    <div className=" overflow-y-auto px-1 pb-4">
      <div style={{ height: collChartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={collectionChartH}
            layout="vertical"
           margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            barSize={14}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="branch"
              width={110}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={(v: number) => `${Number(v).toFixed(0)}%`}
              contentStyle={tooltipStyle}
            />
            <Legend wrapperStyle={legendStyle} />

            <Bar
              dataKey="target"
              name="Target %"
              fill={CHART_COLORS.targetPct}
              radius={[0, 4, 4, 0]}
            />
            <Bar
              dataKey="actual"
              name="Actual %"
              fill={CHART_COLORS.actualPct}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </CardContent>
</Card>
</div>
        </TabsContent>

        {/* Report */}
        <TabsContent value="report">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Target Performance Report — {selectedMonth} {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Branch:{" "}
                <strong className="text-foreground">
                  {selectedBranch === "all"
                    ? "All Branches"
                    : (branches.find((b) => String(b.id) === selectedBranch)?.name ?? "-")}
                </strong>
                {" "} | Period:{" "}
                <strong className="text-foreground">{selectedMonth} {selectedYear}</strong>
              </p>

              {targets.length === 0 && !loading ? (
                <p className="text-center text-muted-foreground py-10">No data for this selection</p>
              ) : (
                <div className="space-y-4">
                  {targets.map((t, i) => {
                    const disbPct = pct(t.disbursementActual, t.disbursementTarget);
                    const leadsPct = pct(t.leadsActual, t.leadsTarget);
                    const custPct = pct(t.customerActual, t.customerTarget);
                    const collPct = t.collectionTarget === 0 ? 0 : Math.round((t.collectionActual / t.collectionTarget) * 100);

                    return (
                      <motion.div key={t.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                        <Card className="border">
                          <CardContent className="p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3">
                              <h3 className="font-semibold text-lg">{t.branch}</h3>
                              {statusBadge(t.disbursementActual, t.disbursementTarget)}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              {[
                                { label: "Disbursement", p: disbPct, detail: `KES ${fmt(t.disbursementActual)} / ${fmt(t.disbursementTarget)}` },
                                { label: "Leads", p: leadsPct, detail: `${t.leadsActual} / ${t.leadsTarget}` },
                                { label: "Customers", p: custPct, detail: `${t.customerActual} / ${t.customerTarget}` },
                                { label: "Collection", p: collPct, detail: `${t.collectionActual}% / ${t.collectionTarget}%` },
                              ].map((m) => (
                                <div key={m.label}>
                                  <div className="flex justify-between text-sm mb-1">
                                    <span className="text-muted-foreground">{m.label}</span>
                                    <span className={cn(
                                      "font-semibold",
                                      m.p >= 100 ? "text-emerald-700" : m.p >= 75 ? "text-amber-700" : "text-red-700"
                                    )}>
                                      {m.p}%
                                    </span>
                                  </div>
                                  <Progress value={Math.min(m.p, 100)} className="h-2" />
                                  <p className="text-xs text-muted-foreground mt-1">{m.detail}</p>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}